import React, { useCallback, useMemo } from 'react';
import { getLocale, history, useParams } from 'umi';
import queryString from 'query-string';
import { Segment } from 'tea-component';

import Service from '@/pages/dashboard/components/PageContainer/Service';
import type { ComponentMetricsItemType } from '@/pages/dashboard/components/PageContainer/type';
import {
  TECHLAND_GAME_CODE,
  TECHLAND_SCOPE_GROSS,
  TECHLAND_SCOPE_NET,
  TECHLAND_STUDIO_CODE,
} from '@home/pages/studio/home/_constant';
import { DATA_TYPE_OBJECT, getName } from '@home/utils/common';
import { ENV, IS_GLOBAL, IS_WOA, LANGUAGE_EN } from '@home/_config/config';
import classNames from 'classnames';
import { omit } from 'lodash-es';
import moment from 'moment';

import StudioTableDownloadShell from '@home/pages/studio/home/components/StudioTableDownloadShell';
import {
  applySpecialDealsFlag,
  mergeSpecialDealsTrackData,
} from '@home/pages/studio/home/utils/specialDealsRequest';
import { computeFilterDateRangeFromEnd } from '@home/pages/studio/home/utils/studioFilterDateRange';

import './index.scss';

type TBundleTitleJumpUrls = {
  woa: string;
  global: string;
};

type TBundleDefaultSortItem = {
  sort_by: string;
  desc?: boolean;
};

const BUNDLE_FILTERS_TEMPLATE: Record<string, unknown> = {
  market_list: ['255'],
  os_list: ['255'],
  category_list: ['255'],
  product_list: ['255'],
  zone_list: ['255'],
  channel_list: ['255'],
  lang_list: ['255'],
};

/** skin_item_overall_performance：仅传 catalog，与接口约定一致；测服 catalog 为 pc_dev_demo，线上/预发为 dl2 */
function getSkinItemApiCatalogList(): string[] {
  if (['test', 'dev'].includes(ENV)) {
    return ['pc_dev_demo'];
  }
  return ['dl2'];
}

/**
 * 标题跳转：WOA / Global 各配一条路由（与 DAU 等 Studio 卡片的站内 link 一致，history.push，不按日期粒度）。
 * 支持 `/dashboard/...` 相对应用路径；若为完整 http(s) 链接仍按环境二选一。
 */
function resolveBundlePerformanceTitleJumpUrl(
  urls: TBundleTitleJumpUrls | undefined,
): string | undefined {
  if (!urls) return undefined;
  const woa = String(urls.woa ?? '').trim();
  const global = String(urls.global ?? '').trim();
  if (IS_WOA && woa) return woa;
  if (IS_GLOBAL && global) return global;
  if (global) return global;
  return woa || undefined;
}

function mergeBundleFilters(
  inputFilters: Record<string, unknown> | undefined | null,
): Record<string, unknown> {
  const input =
    inputFilters && typeof inputFilters === 'object' ? inputFilters : {};
  return {
    ...BUNDLE_FILTERS_TEMPLATE,
    ...input,
    custom_filters: [
      { key: 'catalog', filter_list: getSkinItemApiCatalogList() },
    ],
  };
}

/** 筛选 end_time → 接口 start_date/end_date 用的 YYYY-MM-DD（本地日历日，避免 Date#toISOString 时区偏移） */
function toApiDateString(endTime: unknown): string | null {
  if (endTime === null || endTime === undefined) return null;
  if (endTime instanceof Date || typeof endTime === 'number') {
    const m = moment(endTime);
    return m.isValid() ? m.format('YYYY-MM-DD') : null;
  }
  const s = String(endTime).trim();
  if (!s) return null;
  if (s.length >= 10 && s.charAt(4) === '-' && s.charAt(7) === '-') {
    const iso = moment(s.slice(0, 10), 'YYYY-MM-DD', true);
    return iso.isValid() ? iso.format('YYYY-MM-DD') : null;
  }
  const compact = moment(s, 'YYYYMMDD', true);
  if (compact.isValid()) {
    return compact.format('YYYY-MM-DD');
  }
  const parsed = moment(s);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
}

/**
 * 与 Dashboard NewFilter#getUrlParams 约定一致：start/end + timeType(Day|Week|Month) + rangeDateKey，
 * 子页 IngameRevenueFilter 等可继承 Studio HomeFilter 的日/周/月区间。
 */
function buildStudioFilterQueryForDashboard(
  filter: Record<string, unknown> | undefined,
): Record<string, string> {
  if (!filter) return {};
  const fv = filter as {
    start_time?: unknown;
    end_time?: unknown;
    date_type?: string;
  };
  const endStr = toApiDateString(fv.end_time);
  if (!endStr) return {};
  const startStr = toApiDateString(fv.start_time) || endStr;
  const raw = String(fv.date_type || 'daily').toLowerCase();
  const periodKey =
    raw === 'weekly' || raw === 'monthly' || raw === 'daily' ? raw : 'daily';
  const timeType = DATA_TYPE_OBJECT[periodKey] || DATA_TYPE_OBJECT.daily;
  let rangeDateKey = 'rangeDaily';
  if (periodKey === 'weekly') {
    rangeDateKey = 'rangeWeekly';
  } else if (periodKey === 'monthly') {
    rangeDateKey = 'rangeMonthly';
  }
  return {
    start: startStr,
    end: endStr,
    timeType,
    rangeDateKey,
  };
}

function buildDashboardHrefWithStudioFilter(
  basePath: string,
  filter: Record<string, unknown> | undefined,
): string {
  const q = buildStudioFilterQueryForDashboard(filter);
  if (!Object.keys(q).length) {
    return basePath;
  }
  const qs = queryString.stringify(q, { arrayFormat: 'comma' });
  if (!qs) {
    return basePath;
  }
  const sep = basePath.includes('?') ? '&' : '?';
  return `${basePath}${sep}${qs}`;
}

function parseMetricValue(m: unknown): number | null {
  if (m === null || m === undefined) return null;
  if (typeof m === 'number') return Number.isFinite(m) ? m : null;
  if (typeof m === 'object' && m !== null && 'value' in m) {
    const v = (m as { value?: unknown; }).value;
    if (v === '' || v === null || v === undefined) return null;
    const n = parseFloat(String(v));
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/**
 * 捆绑包表 Date 列：后端约定 YYYYMMDD（或指标对象 `{ value }`），展示 YYYY-MM-DD
 */
function formatBundleTableDate(raw: unknown): string {
  if (raw === null || raw === undefined || raw === '') {
    return '';
  }
  if (typeof raw === 'object' && raw !== null && 'value' in raw) {
    return formatBundleTableDate((raw as { value?: unknown; }).value);
  }
  const s = String(raw).trim();
  const m8 = s.match(/^(\d{8})(?:\.\d+)?$/);
  if (m8) {
    const ds = m8[1];
    return `${ds.slice(0, 4)}-${ds.slice(4, 6)}-${ds.slice(6, 8)}`;
  }
  const m = moment(s, 'YYYYMMDD', true);
  return m.isValid() ? m.format('YYYY-MM-DD') : '';
}

/**
 * Release Date 等：YYYYMMDD、YYYY-MM-DD、数字、带小数位的 8 位日（如 20240405.0）→ YYYY-MM-DD
 */
function normalizeYyyyMmDd(raw: unknown): string {
  if (raw === null || raw === undefined || raw === '') {
    return '';
  }
  if (raw instanceof Date) {
    const m = moment(raw);
    return m.isValid() ? m.format('YYYY-MM-DD') : '';
  }
  if (typeof raw === 'object' && raw !== null && 'value' in raw) {
    return normalizeYyyyMmDd((raw as { value?: unknown; }).value);
  }
  const s0 =
    typeof raw === 'number' && Number.isFinite(raw)
      ? String(Math.floor(raw))
      : String(raw).trim();
  if (!s0) {
    return '';
  }
  // 接口常见：8 位整数 + 小数位（strict YYYYMMDD 会失败）
  const eightWithOptionalFraction = s0.match(/^(\d{8})(?:\.\d+)?$/);
  if (eightWithOptionalFraction) {
    const ds = eightWithOptionalFraction[1];
    return `${ds.slice(0, 4)}-${ds.slice(4, 6)}-${ds.slice(6, 8)}`;
  }
  const compact = moment(s0, 'YYYYMMDD', true);
  if (compact.isValid()) {
    return compact.format('YYYY-MM-DD');
  }
  const iso = moment(s0, 'YYYY-MM-DD', true);
  if (iso.isValid()) {
    return iso.format('YYYY-MM-DD');
  }
  const parsed = moment(s0);
  if (parsed.isValid()) {
    return parsed.format('YYYY-MM-DD');
  }
  if (/^\d{8}$/.test(s0)) {
    return `${s0.slice(0, 4)}-${s0.slice(4, 6)}-${s0.slice(6, 8)}`;
  }
  const isoDay = s0.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoDay) {
    return isoDay[1];
  }
  const n = Number(s0);
  if (
    Number.isFinite(n) &&
    n >= 19000101 &&
    n <= 29991231 &&
    String(Math.floor(n)).length === 8
  ) {
    const ds = String(Math.floor(n));
    return `${ds.slice(0, 4)}-${ds.slice(4, 6)}-${ds.slice(6, 8)}`;
  }
  return '';
}

/** 上线日期（Release Date）：与 `normalizeYyyyMmDd` 相同，由 moment 解析并输出 YYYY-MM-DD */
function normalizeBundleReleaseDate(raw: unknown): string {
  return normalizeYyyyMmDd(raw);
}

/** overall_performance 指标对象上的环比 dod，与 Region / Performance 表一致 */
function parseMetricDod(m: unknown): number | null {
  if (m === null || m === undefined) return null;
  if (typeof m === 'object' && m !== null && 'dod' in m) {
    const d = (m as { dod?: unknown; }).dod;
    if (d === '' || d === null || d === undefined) return null;
    const n = parseFloat(String(d));
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/**
 * 第三层：某一 Scope + 某一 Bundle 子 Tab 下，接口请求字段与表格列写在一起。
 * - metrics / optional logicalFlag / accumulation / singleDate / dateRequestMode → 对应 api 请求体
 * - componentMetrics → CommonTable 列
 */
export type TBundleDateRequestMode = 'single_end' | 'filter_range';

export interface IBundleScopeSegmentConfig {
  metrics: string[];
  /** ?????????? CommonTable ?????????????? */
  defaultSort?: TBundleDefaultSortItem[];
  /** skin_item_overall_performance 等接口可不传 */
  logicalFlag?: string;
  accumulation: Record<string, boolean>;
  /** 累计类 Tab：仅传 end 对应单日，period 固定 daily */
  singleDate?: boolean;
  /**
   * mtx_bundle_info 等：与移动端对齐。
   * - filter_range：默认 period 为 daily；start/end 随 date_type（日/周/月边界见 computeFilterDateRangeFromEnd）
   * - single_end：start=end=筛选结束日，period daily（需与 mtx 区分时使用）
   */
  dateRequestMode?: TBundleDateRequestMode;
  /**
   * Revenue Breakdown 等：与 Home 筛选项日/周/月一致，传 `period: daily|weekly|monthly`；未开启时 filter_range 仍传 daily
   */
  periodFromDateType?: boolean;
  componentMetrics: ComponentMetricsItemType[];
}

/**
 * 第一层 key：HomeFilter `scope`（与 TECHLAND_SCOPE_NET / TECHLAND_SCOPE_GROSS 一致）
 * 第二层 key：Segment Tab 的 value；**对象内键顺序即 Tab 展示顺序**（不再单独配 segmentOptions）
 */
export type TBundleConfigByScope = Record<string, Record<string, IBundleScopeSegmentConfig>>;

/** Filter scope → segment value → 展示文案（建议每个 scope 下写齐各 Tab） */
export type TSegmentOptionTextByScope = Record<
string,
Record<string, { textEn: string; textCn: string; }>
>;

export interface IStudioBundlePerformanceBlockProps {
  data?: Record<string, unknown>;
  changeData?: (key: string, value: unknown) => void;
  children?: React.ReactNode;
  filter?: Record<string, unknown>;
  filterVal?: Record<string, unknown>;
  filterLoading?: boolean;
  api?: string;
  dataKey?: string;
  gameCode?: string;
  studioCode?: string;
  /** scope（与 bundleConfigByScope 第一层 key 一致）→ segment value → 文案（建议写满各 Tab） */
  segmentOptionTextByScope?: TSegmentOptionTextByScope;
  /** scope → segmentTab → 请求参数 + 表格列 */
  bundleConfigByScope: TBundleConfigByScope;
  titleEn?: string;
  titleCn?: string;
  /** 标题站内跳转（与 Studio 趋势卡 link + history.push 一致，不区分日/周/月） */
  titleJumpUrls?: TBundleTitleJumpUrls;
  downloadFileNameEn?: string;
  downloadFileNameCn?: string;
  className?: string;
  style?: React.CSSProperties;
  /** 透传给 Service，开关打开时在请求中追加 special deals logical_flag */
  appendSpecialDealsFlag?: boolean;
}

const DEFAULT_API =
  '/api/v2/dashboard/studio/specific/overall_performance';
const DEFAULT_DATA_KEY = 'bundle_performance_dl2';

/** Tab 展示顺序：累计表现优先，其余按 key 字母序（与配置里对象书写顺序解耦） */
const BUNDLE_SEGMENT_TAB_PRIORITY: Record<string, number> = {
  cumulative_performance: 0,
  net_revenue: 1,
};

function orderBundleSegmentTabKeys(keys: string[]): string[] {
  return [...keys].sort((a, b) => {
    const pa = BUNDLE_SEGMENT_TAB_PRIORITY[a] ?? 99;
    const pb = BUNDLE_SEGMENT_TAB_PRIORITY[b] ?? 99;
    if (pa !== pb) return pa - pb;
    return a.localeCompare(b);
  });
}

/** 筛选项变化时用于整段重置多 Tab Service（与 beforeRequest 依赖的 filter / filterInitData 对齐） */
function buildStudioBlockFilterSignature(
  filter: Record<string, unknown> | undefined,
  filterInitData: unknown,
): string {
  const f = filter ?? {};
  try {
    return JSON.stringify({
      scope: f.scope,
      end_time: f.end_time,
      date_type: f.date_type,
      filterInitData: filterInitData ?? null,
    });
  } catch {
    return `${String(f.scope)}-${String(f.end_time)}`;
  }
}

type TStudioBundlePerformanceTabPaneProps = {
  segmentTab: string;
  isActive: boolean;
  serviceData: Record<string, unknown>;
  bundleConfigByScope: TBundleConfigByScope;
  bundleScopeKey: string;
  gameCode: string;
  studioCode: string;
  api: string;
  dataKey: string;
  filterLoading: boolean;
  formatData: (resData: any, filterVal?: any) => any;
  segment: React.ReactNode;
  titleEn: string;
  titleCn: string;
  titleJumpPath?: string;
  downloadFileNameEn?: string;
  downloadFileNameCn?: string;
  children?: React.ReactNode;
  appendSpecialDealsFlag?: boolean;
};

/** 每 Tab 独立 Service；trackData 不含 Tab，切换不重复请求；筛选项变由外层 key 整段重置 */
const StudioBundlePerformanceTabPane: React.FC<
TStudioBundlePerformanceTabPaneProps
> = ({
  segmentTab,
  isActive,
  serviceData,
  bundleConfigByScope,
  bundleScopeKey,
  gameCode,
  studioCode,
  api,
  dataKey,
  filterLoading,
  formatData,
  segment,
  titleEn,
  titleCn,
  titleJumpPath,
  downloadFileNameEn,
  downloadFileNameCn,
  children,
  appendSpecialDealsFlag,
}) => {
  const branchConfig = bundleConfigByScope[bundleScopeKey]?.[segmentTab];
  const branchMetrics = branchConfig?.componentMetrics || [];
  const defaultTableSorts = useMemo(
    () =>
      (branchConfig?.defaultSort || [])
        .filter(item => item?.sort_by)
        .map(item => ({
          code: item.sort_by,
          order: item.desc === false ? 'asc' : 'desc',
        })),
    [branchConfig?.defaultSort],
  );

  const onTitleDashboardJump = useMemo(() => {
    if (!titleJumpPath) return undefined;
    return () => {
      const href = buildDashboardHrefWithStudioFilter(
        titleJumpPath,
        serviceData.filter as Record<string, unknown> | undefined,
      );
      history.push(href);
    };
  }, [titleJumpPath, serviceData.filter]);

  const beforeRequestTab = useCallback(
    (reqData: any) => {
      const fv = reqData?.filterVal ?? serviceData.filter;
      const filterInitData = reqData?.filterInitData ?? {};
      const scopeKey =
        fv?.scope === TECHLAND_SCOPE_GROSS
          ? TECHLAND_SCOPE_GROSS
          : TECHLAND_SCOPE_NET;
      const tab = segmentTab;
      const cfg = bundleConfigByScope[scopeKey]?.[tab];
      if (!cfg) return false;

      const endTime = fv?.end_time;
      const filtersMerged = mergeBundleFilters(
        filterInitData as Record<string, unknown>,
      );

      let apiStartDate: string;
      let apiEndDate: string;
      let period: string;

      if (cfg.singleDate) {
        const periodDate = toApiDateString(endTime);
        if (!periodDate) return false;
        apiStartDate = periodDate;
        apiEndDate = periodDate;
        period = 'daily';
      } else if (cfg.dateRequestMode === 'filter_range') {
        const range = computeFilterDateRangeFromEnd(endTime, fv?.date_type);
        if (!range) return false;
        apiStartDate = range.start;
        apiEndDate = range.end;
        if (cfg.periodFromDateType) {
          const rawDt = String(fv?.date_type ?? 'daily').toLowerCase();
          if (rawDt === 'weekly') {
            period = 'weekly';
          } else if (rawDt === 'monthly') {
            period = 'monthly';
          } else {
            period = 'daily';
          }
        } else {
          period = 'daily';
        }
      } else {
        const periodDate = toApiDateString(endTime);
        if (!periodDate) return false;
        apiStartDate = periodDate;
        apiEndDate = periodDate;
        period = 'daily';
      }

      return applySpecialDealsFlag(
        {
          game_code: gameCode,
          start_date: apiStartDate,
          end_date: apiEndDate,
          period,
          metrics: cfg.metrics,
          ...(cfg.logicalFlag
            ? { logical_flag: cfg.logicalFlag }
            : {}),
          accumulation: cfg.accumulation,
          page: 1,
          page_size: 999,
          desc: true,
          filters: filtersMerged,
          studio_code: studioCode,
        },
        reqData,
        appendSpecialDealsFlag,
      );
    },
    [
      appendSpecialDealsFlag,
      bundleConfigByScope,
      gameCode,
      segmentTab,
      serviceData.filter,
      studioCode,
    ],
  );

  const mergedData = useMemo(
    () => ({
      ...serviceData,
      bundlePerformanceTab: segmentTab,
    }),
    [segmentTab, serviceData],
  );

  const tableChildren = useMemo(() => {
    return React.Children.map(children, child => {
      if (!React.isValidElement(child)) return child;
      const el = child as React.ReactElement<{
        config?: { props?: Record<string, unknown>; };
      }>;
      const cfg = el.props?.config;
      if (!cfg || typeof cfg !== 'object') return child;
      const baseCprops =
        cfg.props && typeof cfg.props === 'object' ? cfg.props : {};
      const prevWrap =
        (baseCprops.metricsCardWrapProps as Record<string, unknown>) ||
        {};
      const prevCbt =
        (baseCprops.commonBaseTableProps as Record<string, unknown>) ||
        {};
      const prevFeatConfig =
        (prevCbt.featConfig as Record<string, unknown>) || {};
      const prevSortConfig =
        (prevFeatConfig.sort as Record<string, unknown>) || {};
      return React.cloneElement(el, {
        config: {
          ...cfg,
          props: {
            ...baseCprops,
            dataKey:
              (baseCprops.dataKey as string | undefined) ?? dataKey,
            metricsCardWrapProps: {
              ...omit(prevWrap, 'contentHeader'),
              titleEn,
              titleCn,
              ...(isActive ? { contentHeader: segment } : {}),
              ...(onTitleDashboardJump
                ? { titleLink: onTitleDashboardJump }
                : {}),
            },
            commonBaseTableProps: {
              ...prevCbt,
              featConfig: {
                ...prevFeatConfig,
                ...(defaultTableSorts.length
                  ? {
                    sort: {
                      ...prevSortConfig,
                      sorts: defaultTableSorts,
                    },
                  }
                  : {}),
              },
              componentMetrics: branchMetrics,
            },
          },
        },
      });
    });
  }, [
    branchMetrics,
    defaultTableSorts,
    children,
    dataKey,
    segment,
    isActive,
    titleCn,
    titleEn,
    onTitleDashboardJump,
  ]);

  return (
    <div
      className={classNames(
        'dbh-studio-bundle-tab-pane',
        !isActive && 'dbh-studio-bundle-tab-pane--hidden',
      )}
    >
      <Service
        api={api}
        dataKey={dataKey}
        beforeRequest={beforeRequestTab}
        formatData={formatData}
        rawData
        trackData={mergeSpecialDealsTrackData(
          ['filterInitData'],
          appendSpecialDealsFlag,
        )}
        data={mergedData}
        filterLoading={filterLoading}
      >
        <StudioTableDownloadShell
          dataKey={dataKey}
          titleEn={titleEn}
          titleCn={titleCn}
          downloadFileNameEn={downloadFileNameEn}
          downloadFileNameCn={downloadFileNameCn}
        >
          {tableChildren}
        </StudioTableDownloadShell>
      </Service>
    </div>
  );
};

const StudioBundlePerformanceBlock: React.FC<
IStudioBundlePerformanceBlockProps
> = ({
  data: propsData,
  changeData,
  children,
  filter: filterFromParent,
  filterVal: filterValFromParent,
  filterLoading: propsFilterLoading,
  api = DEFAULT_API,
  dataKey = DEFAULT_DATA_KEY,
  gameCode: gameCodeProp,
  studioCode: studioCodeProp,
  segmentOptionTextByScope,
  bundleConfigByScope,
  titleEn = 'Bundle Performance',
  titleCn = 'Bundle Performance',
  titleJumpUrls,
  downloadFileNameEn,
  downloadFileNameCn,
  className,
  style,
  appendSpecialDealsFlag,
}) => {
  const routeParams = useParams() as { studioId?: string; };
  const { studioId } = routeParams;
  const studioCode = studioCodeProp || studioId || TECHLAND_STUDIO_CODE;
  const gameCode = gameCodeProp || TECHLAND_GAME_CODE;
  const isEN = getLocale() === LANGUAGE_EN;

  const serviceData = useMemo((): Record<string, unknown> => {
    const fromPropsData = propsData as
      | { filter?: Record<string, unknown>; }
      | undefined;
    return {
      ...(propsData || {}),
      filter:
        filterFromParent ?? filterValFromParent ?? fromPropsData?.filter,
    };
  }, [propsData, filterFromParent, filterValFromParent]);

  const bundleScopeKey = useMemo(() => {
    const s = (serviceData.filter as { scope?: string; } | undefined)?.scope;
    return s === TECHLAND_SCOPE_GROSS ? TECHLAND_SCOPE_GROSS : TECHLAND_SCOPE_NET;
  }, [serviceData.filter]);

  /** Tab 顺序：累计表现 → 收入拆分（及未列名的其它 Tab） */
  const segmentTabValues = useMemo(
    () =>
      orderBundleSegmentTabKeys(
        Object.keys(bundleConfigByScope[bundleScopeKey] || {}),
      ),
    [bundleConfigByScope, bundleScopeKey],
  );

  const defaultTab = segmentTabValues[0] ?? '';
  const rawSavedTab = propsData?.bundlePerformanceTab as string | undefined;
  const activeTab =
    rawSavedTab && segmentTabValues.includes(rawSavedTab)
      ? rawSavedTab
      : defaultTab;

  const filterSignature = useMemo(
    () =>
      buildStudioBlockFilterSignature(
        serviceData.filter as Record<string, unknown> | undefined,
        serviceData.filterInitData,
      ),
    [serviceData.filter, serviceData.filterInitData],
  );

  const cacheResetKey = `${dataKey}-${gameCode}-${studioCode}-${filterSignature}`;

  const titleJumpPath = useMemo(
    () => resolveBundlePerformanceTitleJumpUrl(titleJumpUrls),
    [titleJumpUrls],
  );

  const tabsToRender = useMemo(
    () =>
      segmentTabValues.length > 0 ? segmentTabValues : [''],
    [segmentTabValues],
  );

  const formatData = useCallback((resData: any, _filterVal?: any) => {
    const list = resData?.metrics_list || [];
    return list.map((item: any) => {
      const metrics = item?.metrics || {};
      const titleText = item?.title ?? item?.product_name ?? item?.product;
      const nameDisplay =
        titleText !== undefined &&
        titleText !== null &&
        String(titleText).trim() !== ''
          ? String(titleText).trim()
          : '-';

      const priceRaw = item?.price;
      let priceVal: number | null = null;
      if (priceRaw !== undefined && priceRaw !== null && String(priceRaw).trim() !== '') {
        const n = parseFloat(String(priceRaw).replace(/,/g, ''));
        priceVal = Number.isFinite(n) ? n : null;
      }

      const row: Record<string, unknown> = {
        product_name: nameDisplay,
        max_dtstatdate: '',
        price: priceVal,
      };

      const rawDate = item?.date;
      row.date = rawDate ? formatBundleTableDate(rawDate) : '';

      Object.keys(metrics).forEach(key => {
        // 日期类指标不用 parseMetricValue
        if (key === 'max_dtstatdate' || key === 'item_release_date') {
          return;
        }
        row[key] = parseMetricValue(metrics[key]);
        const dod = parseMetricDod(metrics[key]);
        if (dod !== null) {
          row[`${key}_dod`] = dod;
        }
      });

      const releaseRaw =
        item?.item_release_date !== undefined && item?.item_release_date !== ''
          ? item.item_release_date
          : item?.max_dtstatdate !== undefined && item?.max_dtstatdate !== ''
            ? item.max_dtstatdate
            : metrics.item_release_date ?? metrics.max_dtstatdate;
      const releaseStr = normalizeBundleReleaseDate(releaseRaw);
      row.max_dtstatdate = releaseStr;
      row.item_release_date = releaseStr;

      return row;
    });
  }, []);

  const teaSegmentOptions = useMemo(
    () =>
      segmentTabValues.map(value => {
        const label = segmentOptionTextByScope?.[bundleScopeKey]?.[value];
        const textEn = label?.textEn ?? '';
        const textCn = label?.textCn ?? '';
        return {
          value,
          text: getName(isEN, textEn, textCn) || value,
        };
      }),
    [bundleScopeKey, isEN, segmentOptionTextByScope, segmentTabValues],
  );

  const segment = useMemo(
    () => (
      <Segment
        className="dbh-studio-bundle-performance-segment"
        value={activeTab}
        onChange={(v: string) => {
          changeData?.('bundlePerformanceTab', v);
        }}
        options={teaSegmentOptions}
      />
    ),
    [changeData, activeTab, teaSegmentOptions],
  );

  return (
    <div
      className={classNames(
        'dbh-studio-bundle-performance-block',
        className,
      )}
      style={style}
    >
      <div key={cacheResetKey} className="dbh-studio-bundle-tab-panes">
        {tabsToRender.map(segmentTab => (
          <StudioBundlePerformanceTabPane
            key={`${dataKey}-${segmentTab}-${bundleScopeKey}`}
            segmentTab={segmentTab}
            isActive={activeTab === segmentTab}
            serviceData={serviceData}
            bundleConfigByScope={bundleConfigByScope}
            bundleScopeKey={bundleScopeKey}
            gameCode={gameCode}
            studioCode={studioCode}
            api={api}
            dataKey={dataKey}
            filterLoading={propsFilterLoading ?? false}
            formatData={formatData}
            segment={segmentTabValues.length > 0 ? segment : null}
            titleEn={titleEn}
            titleCn={titleCn}
            titleJumpPath={titleJumpPath}
            downloadFileNameEn={downloadFileNameEn}
            downloadFileNameCn={downloadFileNameCn}
            appendSpecialDealsFlag={appendSpecialDealsFlag}
          >
            {children}
          </StudioBundlePerformanceTabPane>
        ))}
      </div>
    </div>
  );
};

export default StudioBundlePerformanceBlock;
